import { type User } from '@/features/iam/users/User'
import { type Observable, switchMap } from 'rxjs'
import { type Id } from '@/infra/id'
import type { Workspace } from '@/features/iam/workspaces/Workspace'
import {
  type SnackbarErrorHandlerFactory,
  type SnackbarFactory,
  type SnackbarRetryWrapperFactory,
} from '@/features/shell'
import { createInternalFeatureLoader } from '@/utils/feature-loading-util'
import {
  type GetCurrentMemberFn,
  type GetCurrentWorkspaceFn,
  type PrimeUserFn,
  type PrimeWorkspaceFn,
} from '@/features/iam'
import type { NavigateFn } from '@/RootStore'
import { take } from 'rxjs/operators'

/**
 * Used for showing desktop notifications.
 */
export interface SystemNotification {
  title: string
  body: string
  tag?: string
  icon?: string | null
  timeout?: number
  close$?: Observable<unknown>
  onClick?(): void
}

/**
 * Options for the {@link ActivityFeature}.
 */
export interface ActivityFeatureOptions {
  token$: Observable<string>
  newSnackbar: SnackbarFactory
  createSnackbarErrorHandler: SnackbarErrorHandlerFactory
  createSnackbarRetryWrapper: SnackbarRetryWrapperFactory
  getCurrentUser: () => User
  getWorkspace: (workspaceId: Id) => Workspace
  getCurrentWorkspace: GetCurrentWorkspaceFn
  getCurrentMember: GetCurrentMemberFn
  primeUser: PrimeUserFn
  primeWorkspace: PrimeWorkspaceFn
  systemNotifications$: Observable<SystemNotification>
  navigate: NavigateFn
}

/**
 * Everything related to activity notifications.
 */
export type ActivityFeature = ReturnType<typeof ActivityFeature>

export function ActivityFeature(options: ActivityFeatureOptions) {
  const internal = createInternalFeatureLoader(
    () => import('./internal'),
    (module) => module.InternalActivityFeature(options),
  )

  // Immediately load the module once we have a token;
  // that way we'll be subscribed to desktop notifications
  // as soon as it has loaded.
  options.token$.pipe(take(1), switchMap(internal)).subscribe()

  return {
    // Internal
    internal,

    // Public
    unreadNotificationsCount$: internal.defer(
      (m) => m.unreadNotificationsCount$,
    ),
  }
}
